'use client'

import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react'

import { useOptionalPageAnalytics } from '@/app/context/analytics'
import type { ViewPromotionParams } from '@/app/lib/analytics/types'

/** Umbral que exige el diccionario para las impresiones (`view_promotion`). */
const IMPRESSION_THRESHOLD = 0.5

interface TrackedPromotionProps {
  /** Campos de `view_promotion`/`select_promotion` de esta pieza: ambos eventos
   * llevan exactamente los mismos (`Carousel.csv`, guía 02 §5). */
  readonly promotion: ViewPromotionParams
  /** La pieza ya renderizada (p. ej. `PromoBanner`), con su enlace dentro. */
  readonly children: ReactNode
}

/**
 * Hoja cliente que instrumenta UNA pieza promocional (banner) sin tocarla:
 * - `view_promotion` la primera vez que cruza el 50% del viewport
 *   (`IntersectionObserver`, threshold 0.5; nunca en carga si está fuera de
 *   pantalla), una sola vez mientras siga montada.
 * - `select_promotion` al clicar un enlace real de la pieza (delegación en fase
 *   de captura, sin `preventDefault`: la navegación no se retrasa).
 *
 * Recibe la pieza como `children`, así `PromoBanner` sigue siendo un Server
 * Component. Tolerante fuera de `PageAnalyticsProvider`: no emite nada.
 */
export function TrackedPromotion({ promotion, children }: TrackedPromotionProps) {
  const analytics = useOptionalPageAnalytics()
  const rootRef = useRef<HTMLDivElement | null>(null)
  // El tracker y los params se leen vía ref (actualizada en un efecto, nunca en
  // el render) para que el observer —que se crea una sola vez— use siempre los
  // vigentes sin recrearse.
  const trackViewRef = useRef(analytics?.trackViewPromotion)
  const promotionRef = useRef(promotion)
  useEffect(() => {
    trackViewRef.current = analytics?.trackViewPromotion
    promotionRef.current = promotion
  })

  useEffect(() => {
    const node = rootRef.current
    if (!node) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        trackViewRef.current?.(promotionRef.current)
      },
      { threshold: IMPRESSION_THRESHOLD },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  function handleClickCapture(event: MouseEvent<HTMLDivElement>) {
    const { target } = event
    if (!(target instanceof Element)) return
    if (!target.closest('a[href]')) return
    analytics?.trackSelectPromotion(promotion)
  }

  return (
    <div ref={rootRef} onClickCapture={handleClickCapture}>
      {children}
    </div>
  )
}
